import { Entry, createClient } from 'contentful'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

import {
  PageBanner,
  StatusPageContent,
  OrderPageContent,
  OrderSuccessContent,
  FooterLink,
  FooterContent,
  FooterLinkBlock
} from '@/types'

import { AreaStatus, AreaType, DeliveryOptions } from '@/constants/operator'
import { CfAnslPageLanding } from '@/types/ansl-space/types/cf-ansl-page-landing'
import { CfAnslNoticeOfInterest } from '@/types/ansl-space/types/cf-ansl-notice-of-interest'
import { CfAnslSimplePage } from '@/types/ansl-space/types/cf-ansl-simple-page'
import { CfAnslPageBanner } from '@/types/ansl-space/types/cf-ansl-page-banner'
import { CfAnslPageSupport } from '@/types/ansl-space/types/cf-ansl-page-support'
import { CfAnslPageServiceProviders } from '@/types/ansl-space/types/cf-ansl-page-service-providers'
import { CfAnslAddressSearch } from '@/types/ansl-space/types/cf-ansl-address-search'
import { CfAnslAreaProduct } from '@/types/ansl-space/types/cf-ansl-area-product'
import {
  CfAnslAreaProductStatusPage
} from '@/types/ansl-space/types/cf-ansl-area-product-status-page'
import {
  CfAnslProductInstallationStep
} from '@/types/ansl-space/types/cf-ansl-product-installation-step'
import { CfAnslRedirectModal } from '@/types/ansl-space/types/cf-ansl-redirect-modal'
import { CfAnslContactForm } from '@/types/ansl-space/types/cf-ansl-contact-form'
import { CfAnslFooter } from '@/types/ansl-space/types/cf-ansl-footer'
import { CfAnslFooterColumn } from '@/types/ansl-space/types/cf-ansl-footer-column'
import { CfAnslButton } from '@/types/ansl-space/types/cf-ansl-button'
import { CfAnslCookieConsent } from '@/types/ansl-space/types/cf-ansl-cookie-consent'
import { CfAnslLandlordOrder } from '@/types/ansl-space/types/cf-ansl-landlord-order'

type AreaProductsPageContentPayload = {
  areaType: string;
  areaStatus: string;
  deliveryOption?: string;
}

const areaProductOrderPageContentIdsByTypeAndStatus: Record<string, Record<string, string>> = {
  [AreaType.Upsell]: {
    [AreaStatus.SaleInProgress]: '6kmTa126hOaXYHPxZAhDJ',
    [AreaStatus.BuildingInProgress]: '5hvM3kCyXwVcxGoAKxNnbV'
  },
  [AreaType.Fwa]: {
    [AreaStatus.SaleInProgress]: 'fU0QDHpl395jmNXAuqQLv',
    [AreaStatus.BuildingInProgress]: '2SpwF2PxtCnqyHn479cn7Q'
  },
  [AreaType.Rural]: {
    [AreaStatus.SaleInProgress]: 'QaxGj6iyVYhYStN5Tjmmm',
    [AreaStatus.BuildingInProgress]: '5Z4PlfE76cOtbnTOjSKNCR'
  },
  [AreaType.FwaLowband]: {
    [AreaStatus.SaleInProgress]: '1bUlECi7LxdAXiteewENwL',
    [AreaStatus.BuildingInProgress]: '4nuJ97fo87D7Kq52B0eQRA'
  },
  [AreaType.FwaSiteDevelopment]: {
    [AreaStatus.SaleInProgress]: '2p7lrpoloHQJQUl0KSOzrN',
    [AreaStatus.BuildingInProgress]: '10uI7o2iA5LuRZSwRx7Od1'
  },
  [AreaType.UpsellOperatorChange]: {
    [AreaStatus.SaleInProgress]: '4edpt9NBl5EIE9GW9QjhY3',
    [AreaStatus.BuildingInProgress]: '61n9KxZG2XOPGv9td4bQjj',
    [AreaStatus.UpsellOperatorChangeNoCampaign]: '1wxtUZBhRoJP8S2w6KZThN'
  }
}

const areaProductStatusPageContentIdsByTypeAndStatus: Record<string, Record<string, string>> = {
  [AreaType.Upsell]: {
    [AreaStatus.SaleInProgress]: '4VHc2HD0UxMmVpioLf3rnE',
    [AreaStatus.BuildingInProgress]: '5RX1BAPPZrXp0lmEbPsQAg'
  },
  [AreaType.Fwa]: {
    [AreaStatus.SaleInProgress]: '335f5pd7vOHPmbVGWVDQrs',
    [AreaStatus.BuildingInProgress]: '3eUnfOxV6x98S90FbmTRxO'
  },
  [AreaType.Rural]: {
    [AreaStatus.SaleInProgress]: '3KuBkoC98rpt2BPDtYjM58',
    [AreaStatus.BuildingInProgress]: 'IJgBz7XhD8W7DkwOpV50r'
  },
  [AreaType.FwaLowband]: {
    [AreaStatus.SaleInProgress]: '79gdrJJy227dVllxyarGQ',
    [AreaStatus.BuildingInProgress]: '7x5WYBblusgTb4bzhXgct6'
  },
  [AreaType.FwaSiteDevelopment]: {
    [AreaStatus.SaleInProgress]: '7f5I5m8AMU898Cp7gNm1O0',
    [AreaStatus.BuildingInProgress]: '18RjJnxQV1GPJ7PQmgDuil'
  },
  [AreaType.UpsellOperatorChange]: {
    [AreaStatus.SaleInProgress]: '102IIPIqZt53m75a5xWvUl',
    [AreaStatus.BuildingInProgress]: '4bNOppL9AyNcUcqcEb7PHS',
    [AreaStatus.UpsellOperatorChangeNoCampaign]: '3M2cP20IRCxxVDHHggkMYE'
  }
}

const areaProductOrderSuccessContentIdsByType: Record<string, string> = {
  [AreaType.Upsell]: '3Qo2hB9gYfyCuFracJ39Xw',
  [AreaType.Fwa]: 'gE66HmNmRkMrRgQAfR3bY',
  [AreaType.FwaLowband]: 'oDL0vONnSNgerJF63JDEr',
  [AreaType.FwaSiteDevelopment]: '1pLSYvIIi4CMzVbzHWeMe0',
  [AreaType.Rural]: '3a4GtvLWIxg2IPpqNaqYKo',
  [AreaType.UpsellOperatorChange]: '2efBgyKgZ44jK2onP8Wxf2'
}

const noticeOfInterestContentIdsByStatus: Record<string, string> = {
  [AreaStatus.RuralInterest]: '2ORTxviPQHWLvQG2Gl670U',
  [AreaStatus.RuralInterestAfterBuilding]: '2MkZdl0M64iQcFWex7PmCJ',
  [AreaStatus.RuralInterestAfterSalesQuote]: '2kq3GepBVhBPq9s878Rhf1',
  [AreaStatus.RuralInterestAfterSalesSuc]: '2jNbxk1KqysaOpNzV2e6Uq',
  [AreaStatus.FwaInterest]: '793sNjASyVHSG74wZkrrlI',
  [AreaStatus.FwaInterestAfterBuilding]: '7qrGBXDonFFLlrDPU0qPOg',
  [AreaStatus.FwaLowbandInterest]: 'WAmGWiYemelYKp8aWxP7k',
  [AreaStatus.FwaSiteDevelopmentInterest]: '6riVEQP4BPurF1CFb6SUKW',
  [AreaStatus.FwaSiteDevelopmentInterestAfterBuilding]: '21HFMaecaSQvAb3JwpqgQ9'
}

const isDev = process.env.NODE_ENV === 'development'
const HOST = isDev ? process.env.VUE_APP_CONTENTFUL_WRAPPER_HOST : window.location.host

const client = createClient({
  environment: process.env.VUE_APP_CONTENTFUL_ENV,
  space: process.env.VUE_APP_CONTENTFUL_SPACE,
  accessToken: '_',
  host: HOST,
  basePath: process.env.VUE_APP_CONTENTFUL_WRAPPER_BASE_PATH
})

export const getFooterContent = async (): Promise<FooterContent> => {
  const footerContent = await client.getEntry('1MV6a8ZRyfpwXsXfb7qfp2', { 'include': 2 }) as Entry<CfAnslFooter>

  const createFooterLink = (title: string, url: string, simplePagePath: string): FooterLink => {
    return { title, url, simplePagePath }
  }

  /* eslint-disable no-extra-parens */
  const linkMapper = (link: Entry<CfAnslButton | CfAnslSimplePage>): FooterLink => {
    if (link.sys.contentType.sys.id === 'anslSimplePage') {
      const fields = link.fields as CfAnslSimplePage
      return createFooterLink(fields.pageIdentifier, '', `/${fields.slug}`
      )
    }

    const fields = link.fields as CfAnslButton
    return createFooterLink(fields.title, fields.link || '', ''
    )
  }

  const navigation = footerContent.fields.navigationLinks.map((column: Entry<CfAnslFooterColumn>): FooterLinkBlock => {
    return {
      title: column.fields.title,
      links: column.fields.links?.map(linkMapper) || []
    }
  })
  const bottomLinks = footerContent.fields.bottomLinks.map(linkMapper)

  return {
    navigation,
    bottomLinks
  }
}

export const getLandingPageContent = async (): Promise<Entry<CfAnslPageLanding>> => {
  return client.getEntry('ku1yT2TuCS3pIRJTJ82lS', { 'include': 2 })
}

export const getLandlordPageContent = async (): Promise<Entry<CfAnslPageLanding>> => {
  return await client.getEntry('6l6waRaUpZMvv6cULddCHm', { 'include': 2 })
}

export const getLandlordContactFormContent = async (): Promise<Entry<CfAnslContactForm>> => {
  return client.getEntry('1twT16WsN0H2z5DBddvnMI', { 'include': 2 })
}

export const getLandlordOrderContent = async (): Promise<Entry<CfAnslLandlordOrder>> => {
  return client.getEntry('19GH5lYtrJhcvlDleyU1Jn', { 'include': 2 })
}

export const getSupportPageContent = async (): Promise<Entry<CfAnslPageSupport>> => {
  return client.getEntry('4YgxbHauMAEJopFkQu1jcL', { 'include': 3 })
}

export const getContactFormPageContent = async (): Promise<Entry<CfAnslContactForm>> => {
  return client.getEntry('1B1g3EQ5tIuHvAR8QowUHj', { 'include': 3 })
}

export const getWhereDoYouLivePageContent = async (): Promise<Omit<PageBanner, 'paragraph'>> => {
  const entry = await client.getEntry('7uCn3dzdev5wFbpZJz3WOl') as Entry<CfAnslPageBanner>

  const entryDescription = entry.fields.description
  return {
    title: entry.fields.title,
    description: entryDescription ? documentToHtmlString(entryDescription) : ''
  }
}

export const getServiceProvidersPageContent = async (): Promise<Entry<CfAnslPageServiceProviders>> => {
  return await client.getEntry('2YRHI892u3db4RwlG2Qqtz')
}

export const getSimplePageContent = async (path: string): Promise<CfAnslSimplePage> => {
  const pageEntries = await client.getEntries({ 'content_type': 'anslSimplePage' })

  // eslint-disable-next-line no-extra-parens
  const pageEntry = pageEntries.items.find(item => (item.fields as CfAnslSimplePage).slug === path)

  return pageEntry?.fields as CfAnslSimplePage
}

export const getNoticeOfInterestGenericPageContent = async (): Promise<Entry<CfAnslNoticeOfInterest>> => {
  return client.getEntry('3MhYrzAaCJHIJBh4cNtkbO', { 'include': 2 })
}

export const getNoticeOfInterestUpsellPageContent = async (): Promise<Entry<CfAnslNoticeOfInterest>> => {
  return client.getEntry('5dmuONNHqqZS5Ki6CNV5xt', { 'include': 2 })
}

export const getNoticeOfInterestByAreaStatusContent = async (areaStatus: AreaStatus): Promise<Entry<CfAnslNoticeOfInterest>> => {
  return client.getEntry(noticeOfInterestContentIdsByStatus[areaStatus], { 'include': 2 })
}

export const getNoticeOfInterestOperatorChangePageContentUpsell = async (): Promise<Entry<CfAnslNoticeOfInterest>> => {
  return client.getEntry('15cvLALNZaX0NrwOaQnXKL', { 'include': 2 })
}

export const getRedirectModalContent = async (): Promise<Entry<CfAnslRedirectModal>> => {
  return client.getEntry('7qdH0LyvyO4kaP8iDiu0DG', { 'include': 2 })
}

export const getStatusPageContent = async ({ areaType, areaStatus, deliveryOption }: AreaProductsPageContentPayload): Promise<StatusPageContent> => {
  const response: Entry<CfAnslAreaProductStatusPage> = await client.getEntry(areaProductStatusPageContentIdsByTypeAndStatus[areaType][areaStatus], { 'include': 2 })
  const deliveryOptionSelector = deliveryOption === DeliveryOptions.ExpressDelivery ? 'installationStepsExpressDelivery' : 'installationStepsPlannedDelivery'

  return {
    steps: response.fields[deliveryOptionSelector] as Entry<CfAnslProductInstallationStep>[],
    banner: {
      backgroundImage: response.fields.banner.fields.backgroundImage?.fields?.file?.url,
      title: response.fields.banner.fields.title,
      description: response.fields.banner.fields.description,
      paragraph: response.fields.banner.fields.paragraph
    },
    pageBottomText: response.fields.statusInfo?.fields.text
  }
}

export const getOrderPageContent = async ({ areaType, areaStatus }: AreaProductsPageContentPayload): Promise<OrderPageContent> => {
  const response: Entry<CfAnslAreaProduct> = await client.getEntry(areaProductOrderPageContentIdsByTypeAndStatus[areaType][areaStatus], { 'include': 2 })

  return {
    banner: {
      backgroundImage: response.fields.banner.fields.backgroundImage?.fields?.file?.url,
      title: response.fields.banner.fields.title,
      description: response.fields.banner.fields.description,
      paragraph: response.fields.banner.fields.paragraph
    },
    hardwareAddressTooltip: response.fields.hardwareAddressTooltip,
    termsInfo: response.fields.termsInfo
  }
}

export const getOrderSuccessContent = async ({ areaType }: AreaProductsPageContentPayload): Promise<OrderSuccessContent> => {
  const response: Entry<CfAnslPageBanner> = await client.getEntry(areaProductOrderSuccessContentIdsByType[areaType], { 'include': 2 })

  return {
    banner: {
      backgroundImage: response.fields.backgroundImage?.fields?.file?.url,
      title: response.fields.title,
      description: response.fields.description,
      paragraph: response.fields.paragraph
    }
  }
}

export const getAddressSearchContent = async (): Promise<Entry<CfAnslAddressSearch>> => {
  return client.getEntry('4n0sLlJwrxW7Z1ALyPKPYO', { 'include': 2 })
}
export const getCookieConsentContent = async (): Promise<Entry<CfAnslCookieConsent>> => {
  return client.getEntry('6bagKEfcHFPHOpQd6acjQ9', { 'include': 2 })
}
